import type { CodeLanguage } from '@mysaiva/core/components'
import type { DocBlock } from './types'

/**
 * The Markdown the backend writes into its OpenAPI document (the overview in
 * `info.description`, endpoint / parameter / field descriptions), parsed into
 * the documentation's own block and inline types so pages render it with the
 * workspace's components instead of a generic Markdown renderer.
 *
 * Blocks: paragraphs, `#`–`###` headings, bullet and numbered lists, pipe
 * tables, fenced code. Inline: `code`, **bold** / __bold__, *italic* /
 * _italic_, [links](https://…). Anything else stays plain text.
 */

export type InlineNode =
  | { type: 'text'; text: string }
  | { type: 'code'; text: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'link'; href: string; children: InlineNode[] }

export type MarkdownNode =
  | { type: 'heading'; level: number; text: string }
  | Exclude<DocBlock, { type: 'heading' }>

const CODE_LANGUAGES: CodeLanguage[] = ['json', 'bash', 'http', 'text']

/**
 * Only web, relative and in-page links; anything else (e.g. `javascript:`, or
 * a protocol-relative `//host` that would pass for relative) renders as text.
 */
export function safeHref(href: string): string | null {
  const trimmed = href.trim()
  return /^(https?:\/\/|\/(?!\/)|#)/i.test(trimmed) ? trimmed : null
}

/**
 * Inline Markdown → nodes. A delimiter only opens when its closing partner
 * exists; `_` emphasis needs word boundaries so `snake_case_names` stay text.
 */
export function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = []
  let plain = ''
  const flush = () => {
    if (plain) nodes.push({ type: 'text', text: plain })
    plain = ''
  }

  let i = 0
  while (i < text.length) {
    const rest = text.slice(i)
    let match: RegExpMatchArray | null

    if ((match = rest.match(/^`([^`]+)`/))) {
      flush()
      nodes.push({ type: 'code', text: match[1] })
    } else if ((match = rest.match(/^\*\*(?=\S)([\s\S]*?\S)\*\*/) ?? rest.match(/^__(?=\S)([\s\S]*?\S)__(?!\w)/))) {
      if (match[0].startsWith('__') && /\w/.test(text[i - 1] ?? '')) {
        plain += match[0][0]
        i++
        continue
      }
      flush()
      nodes.push({ type: 'strong', children: parseInline(match[1]) })
    } else if ((match = rest.match(/^\*(?=[^\s*])([^*]*?[^\s*])\*/) ?? rest.match(/^_(?=[^\s_])([^_]*?[^\s_])_(?!\w)/))) {
      if (match[0].startsWith('_') && /\w/.test(text[i - 1] ?? '')) {
        plain += '_'
        i++
        continue
      }
      flush()
      nodes.push({ type: 'em', children: parseInline(match[1]) })
    } else if ((match = rest.match(/^\[([^\]]+)\]\(([^)\s]+)\)/))) {
      const href = safeHref(match[2])
      flush()
      if (href) nodes.push({ type: 'link', href, children: parseInline(match[1]) })
      else nodes.push(...parseInline(match[1]))
    } else {
      plain += text[i]
      i++
      continue
    }
    i += match[0].length
  }
  flush()
  return nodes
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export { slugify as markdownSlug }

export function codeLanguage(info: string, code: string): CodeLanguage {
  const language = info.trim().toLowerCase() as CodeLanguage
  if (CODE_LANGUAGES.includes(language)) return language
  // Unlabelled fences in the backend docs are request lines and headers.
  return /^(GET|POST|PUT|PATCH|DELETE) |^[\w-]+: /m.test(code) ? 'http' : 'text'
}

function tableCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim())
}

const isTableRow = (line: string) => line.trim().startsWith('|')
// GFM accepts a single dash per column (`|-|-|`).
const isTableDivider = (line: string) => /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?$/.test(line.trim())
const BULLET = /^\s*[-*+] +/
const NUMBERED = /^\s*\d+[.)] +/
const HEADING = /^(#{1,6}) +(.*)$/

/**
 * Block Markdown → nodes. Headings keep their level so callers decide what a
 * `##` means (a page section in the overview); code blocks keep their
 * language. List items continue over indented lines.
 */
export function parseMarkdown(markdown: string): MarkdownNode[] {
  const nodes: MarkdownNode[] = []
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')

  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const heading = line.match(HEADING)

    if (heading) {
      nodes.push({ type: 'heading', level: heading[1].length, text: heading[2].trim() })
      i++
    } else if (line.trimStart().startsWith('```')) {
      const info = line.trimStart().slice(3)
      const code: string[] = []
      i++
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) code.push(lines[i++])
      i++ // closing fence
      const text = code.join('\n')
      nodes.push({ type: 'code', title: '', code: text, language: codeLanguage(info, text) })
    } else if (isTableRow(line) && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      const columns = tableCells(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && isTableRow(lines[i])) rows.push(tableCells(lines[i++]))
      // Cells keep their Markdown; DocInline renders it.
      nodes.push({ type: 'table', table: { columns, rows, codeColumns: [] } })
    } else if (BULLET.test(line) || NUMBERED.test(line)) {
      const marker = BULLET.test(line) ? BULLET : NUMBERED
      const items: string[] = []
      while (i < lines.length && marker.test(lines[i])) {
        let item = lines[i++].replace(marker, '')
        // Indented continuation lines belong to the item.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !BULLET.test(lines[i]) && !NUMBERED.test(lines[i])) {
          item += ` ${lines[i++].trim()}`
        }
        items.push(item.trim())
      }
      nodes.push({ type: 'bullets', items, ordered: marker === NUMBERED || undefined })
    } else if (line.trim() === '') {
      i++
    } else {
      // The first line always belongs to the paragraph, so every pass consumes
      // at least one line (e.g. a `|` line without a table divider after it).
      const text: string[] = [lines[i++].trim()]
      while (
        i < lines.length &&
        lines[i].trim() !== '' &&
        !HEADING.test(lines[i]) &&
        !lines[i].trimStart().startsWith('```') &&
        !isTableRow(lines[i]) &&
        !BULLET.test(lines[i]) &&
        !NUMBERED.test(lines[i])
      ) {
        text.push(lines[i++].trim())
      }
      nodes.push({ type: 'paragraph', text: text.join(' ') })
    }
  }
  return nodes
}

/**
 * A description (endpoint, parameter, field) as documentation blocks, for the
 * main column: headings become sub-headings, code stays inline in the flow.
 */
export function markdownToBlocks(markdown: string): DocBlock[] {
  return parseMarkdown(markdown).map(node =>
    node.type === 'heading' ? { type: 'heading', text: node.text } : node
  )
}
